(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.add('psv-js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isBulgarian = root.lang.toLowerCase().startsWith('bg');
  const observedAccountRoots = new WeakSet();
  let revealObserver;

  const normalizeInternalLinks = (scope = document) => {
    scope.querySelectorAll('a[href]').forEach((anchor) => {
      const rawHref = anchor.getAttribute('href');
      if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

      try {
        const url = new URL(rawHref, window.location.origin);
        if (url.hostname === 'xsv6uk-gd.myshopify.com') {
          anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
        }
      } catch (_) {
        // Leave malformed or app-owned links untouched.
      }
    });
  };

  const correctVisibleLabels = (scope = document) => {
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      if (!node.nodeValue) return;
      node.nodeValue = node.nodeValue
        .replaceAll('Mонтаж', 'Монтаж')
        .replaceAll('Правила за повелителност', 'Политика за поверителност');
    });
  };

  const replaceAccountLinkLabel = (link, label) => {
    const textNode = Array.from(link.childNodes).find((node) => (
      node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()
    ));
    if (textNode?.nodeValue.trim() !== label) {
      if (textNode) textNode.nodeValue = ` ${label}`;
      else link.append(document.createTextNode(` ${label}`));
    }
    link.setAttribute('aria-label', label);
  };

  const localizeAccountElement = (account) => {
    if (!isBulgarian || !account.shadowRoot) return;
    const accountRoot = account.shadowRoot;

    accountRoot.querySelectorAll('a[href*="/account/orders"]').forEach((link) => {
      replaceAccountLinkLabel(link, 'Поръчки');
    });
    accountRoot.querySelectorAll('a[href*="/account/profile"]').forEach((link) => {
      replaceAccountLinkLabel(link, 'Профил');
    });

    const loginRoot = accountRoot.querySelector('shopify-login-form')?.shadowRoot;
    if (loginRoot) {
      loginRoot.querySelectorAll('.login-form__divider-text').forEach((label) => {
        if (label.textContent !== 'ИЛИ') label.textContent = 'ИЛИ';
      });
      loginRoot.querySelectorAll('.login-form__divider').forEach((divider) => {
        divider.setAttribute('aria-label', 'ИЛИ');
      });
    }

    [accountRoot, loginRoot].filter(Boolean).forEach((shadowRoot) => {
      if (observedAccountRoots.has(shadowRoot)) return;
      const observer = new MutationObserver(() => localizeAccountElement(account));
      observer.observe(shadowRoot, { childList: true, subtree: true });
      observedAccountRoots.add(shadowRoot);
    });
  };

  const localizeShopifyAccounts = () => {
    if (!isBulgarian) return;
    document.querySelectorAll('shopify-account').forEach(localizeAccountElement);
  };

  const scheduleAccountLocalization = () => {
    if (!isBulgarian) return;
    [0, 80, 220, 500, 1000].forEach((delay) => {
      window.setTimeout(localizeShopifyAccounts, delay);
    });
  };

  const collapseEmptyMedia = (image) => {
    image.hidden = true;
    image.setAttribute('aria-hidden', 'true');
    const wrapper = image.closest('picture, figure');
    if (wrapper && !wrapper.querySelector('img:not([hidden]), video, iframe')) wrapper.hidden = true;
  };

  const guardLegacyImages = (scope = document) => {
    scope.querySelectorAll('img').forEach((image) => {
      const source = image.currentSrc || image.getAttribute('src') || '';
      if (source.includes('/woodmart/images/lazy.svg')) {
        collapseEmptyMedia(image);
        return;
      }

      if (!image.dataset.psvErrorGuard) {
        image.dataset.psvErrorGuard = 'true';
        image.addEventListener('error', () => collapseEmptyMedia(image), { once: true });
      }
    });
  };

  const prefillProductInquiry = () => {
    const productHandle = new URLSearchParams(window.location.search).get('product');
    if (!productHandle || !document.body.classList.contains('psv-template-suffix-contact')) return;

    const textarea = document.querySelector('textarea[id^="ContactFormMessage-"], form[action*="/contact"] textarea');
    if (!textarea || textarea.dataset.psvProductContext) return;

    const safeHandle = productHandle.replace(/[^a-z0-9_-]/gi, '');
    if (!safeHandle) return;
    const productUrl = `${window.location.origin}/products/${safeHandle}`;
    const contextLine = `Имам въпрос за този продукт: ${productUrl}`;
    if (!textarea.value.includes(productUrl)) {
      textarea.value = textarea.value.trim() ? `${contextLine}\n\n${textarea.value}` : `${contextLine}\n\n`;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    textarea.dataset.psvProductContext = 'true';
  };

  const enhanceStoreDirectory = () => {
    if (!document.body.classList.contains('psv-template-suffix-magazini')) return;

    const stores = Array.from(document.querySelectorAll('.text-image__cards__item'));
    if (!stores.length) return;
    const cities = new Map();

    stores.forEach((store, index) => {
      const title = store.querySelector('.block-title-size, .heading--small, .heading--medium');
      const titleText = title?.textContent.trim();
      if (!titleText) return;

      const storeId = `magazin-${index + 1}`;
      store.id = storeId;
      const isWarehouse = store.classList.contains('psv-location-card--warehouse') || /склад/i.test(titleText);
      const city = titleText
        .split(/[—·]/)[0]
        .replace(/\s+\d+$/, '')
        .trim();
      if (!isWarehouse) {
        if (!cities.has(city)) cities.set(city, { id: storeId, count: 0 });
        const cityData = cities.get(city);
        cityData.count += 1;
      }

      if (store.dataset.psvStoreEnhanced) return;
      const mapSource = store.querySelector('.text-image__cards__item-image a[href*="maps"], .text-image__cards__item-image a[href*="google.com"]');
      const content = store.querySelector('.text-image__cards__item-content');
      if (mapSource && content) {
        const mapLink = document.createElement('a');
        mapLink.className = 'psv-store-map-link';
        mapLink.href = mapSource.href;
        mapLink.target = '_blank';
        mapLink.rel = 'noopener';
        mapLink.textContent = 'Отвори в Google Maps';
        mapLink.dataset.event = 'store_map_click';
        mapLink.dataset.eventLabel = titleText;
        content.append(mapLink);
      }
      store.dataset.psvStoreEnhanced = 'true';
    });

    const header = document.querySelector('.psv-page--magazini .psv-page__header');
    if (!header || document.querySelector('.psv-store-jump')) return;
    const nav = document.createElement('nav');
    nav.className = 'psv-store-jump';
    nav.setAttribute('aria-label', 'Избери град');

    const navHeading = document.createElement('strong');
    navHeading.className = 'psv-store-jump__heading';
    navHeading.textContent = 'Избери град';
    nav.append(navHeading);

    const cityGrid = document.createElement('div');
    cityGrid.className = 'psv-store-jump__grid';
    cities.forEach(({ id, count }, city) => {
      const link = document.createElement('a');
      link.href = `#${id}`;
      const cityName = document.createElement('strong');
      cityName.textContent = city;
      const cityCount = document.createElement('span');
      cityCount.textContent = `${count} ${count === 1 ? 'магазин' : 'магазина'}`;
      link.append(cityName, cityCount);
      cityGrid.append(link);
    });
    nav.append(cityGrid);
    header.append(nav);
  };

  const cleanLegacyArticleMarkup = (scope = document) => {
    const article = scope.querySelector?.('.main-article__text .page__body') ||
      (scope.matches?.('.main-article__text .page__body') ? scope : null);
    if (!article || article.dataset.psvLegacyCleaned) return;

    const shortcodePattern = /\[(?:\/?vc_[^\]]*|\/?et_pb_[^\]]*|gallery[^\]]*|\/?row[^\]]*)\]/gi;
    const walker = document.createTreeWalker(article, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (node.parentElement?.closest('script, style')) return;
      node.nodeValue = node.nodeValue.replace(shortcodePattern, '').replace(/\s{3,}/g, ' ');
    });
    article.querySelectorAll('p, div').forEach((node) => {
      if (!node.textContent.trim() && !node.querySelector('img, video, iframe, a')) node.remove();
    });
    article.dataset.psvLegacyCleaned = 'true';
  };

  const decodeVisibleEntities = () => {
    const decoder = document.createElement('textarea');
    document.querySelectorAll('.main-article h1, .main-article__title, .psv-blog-card h2').forEach((node) => {
      if (!/&(?:#\d+|#x[\da-f]+|[a-z]+);/i.test(node.textContent)) return;
      decoder.innerHTML = node.textContent;
      node.textContent = decoder.value;
    });
  };

  const reveal = (scope = document) => {
    const items = scope.querySelectorAll('[data-reveal]:not([data-psv-reveal-ready])');
    items.forEach((item) => {
      item.dataset.psvRevealReady = 'true';
      if (reducedMotion || !('IntersectionObserver' in window)) {
        item.classList.add('is-visible');
      } else {
        revealObserver.observe(item);
      }
    });
  };

  const enhance = (scope = document) => {
    normalizeInternalLinks(scope);
    correctVisibleLabels(scope);
    guardLegacyImages(scope);
    prefillProductInquiry();
    enhanceStoreDirectory();
    cleanLegacyArticleMarkup(scope);
    decodeVisibleEntities();
    reveal(scope);
  };

  const start = () => {
    if (!reducedMotion && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    enhance(document);
    scheduleAccountLocalization();

    document.addEventListener('click', (event) => {
      if (event.composedPath().some((node) => node?.localName === 'shopify-account')) {
        scheduleAccountLocalization();
      }
      const target = event.target.closest('[data-event]');
      if (!target) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: target.dataset.event,
        event_label: target.dataset.eventLabel || '',
        link_url: target.href || '',
        psv_path: window.location.pathname
      });
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) enhance(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* psv store anchor (seed-986) — #magazin-N води до N-тата ВИДИМА картичка на /pages/magazini.

   ПОПРАВЕН 2026-09-28T15:5xZ след обратна връзка от Диан. Първата версия (28.09T15:0xZ) търсеше
   .store-locator__content__item — ГРЕШНИЯТ списък: тези 14 елемента живеят в СКРИТ изскачащ
   прозорец (store-locator-popup__container … position:fixed, x-cloak, x-show="show"), а
   scrollIntoView върху скрит fixed елемент не мести страницата. Видимите картички, които
   клиентът вижда, са .text-image__cards__item.psv-location-card и точно те получават
   id="magazin-N" от enhanceStoreDirectory по-горе в този файл.

   КАКВО СЕ Е ВИЖДАЛО: при /pages/magazini#magazin-6 Диан докладва „Отваря се по-скоро Варна" —
   страницата спираше ПОД Пловдив (картичката на Пловдив отрязана най-горе, Варна в средата).
   Причината не е номерацията: редът е премерен на живо и в двата списъка е един и същ
   (1-5 София, 6 Пловдив, 7 Варна, 8 Варна, 9 Бургас, 10 В. Търново, 11 Айтос, 12 Несебър,
   13 Шумен, 14 Ямбол). Причината е РАЗМЕСТВАНЕ СЛЕД СКРОЛА: id-тата се раждат чак на
   DOMContentLoaded, а над мрежата стоят локаторът (Alpine, x-cloak) и мързеливи снимки — щом
   те се подредят, целта се мести и еднократният скрол остава на грешно място.

   ЗАТОВА скролът тук се ПОТВЪРЖДАВА до 4 секунди и се отказва в мига, в който човекът скролне
   сам — не се борим с него. Височината на залепената лента се вади, за да не реже заглавието. */
(function () {
  var STEP_MS = 120;
  var MAX_MS = 4000;
  var GAP_PX = 16;
  var TOLERANCE_PX = 3;
  var timer = null;
  var abandoned = false;

  function wantedNumber() {
    var m = /^#magazin-(\d+)$/.exec(window.location.hash || '');
    if (!m) return -1;
    var n = parseInt(m[1], 10);
    return n >= 1 ? n : -1;
  }

  function cardFor(n) {
    var byId = document.getElementById('magazin-' + n);
    if (byId) return byId;
    /* enhanceStoreDirectory още не е минал: същият ред, същата номерация (1-базирана) */
    var cards = document.querySelectorAll('.text-image__cards__item.psv-location-card');
    return cards.length >= n ? cards[n - 1] : null;
  }

  function scrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function headerOffset() {
    var bar = document.querySelector('.sticky-header-content, .x-header-content, header');
    if (!bar) return 0;
    var box = bar.getBoundingClientRect();
    /* вади се само ако лентата наистина е залепена най-горе в този момент */
    return box.top <= 1 ? box.height : 0;
  }

  function desiredTop(card) {
    var top = card.getBoundingClientRect().top + scrollY();
    return Math.max(0, Math.round(top - headerOffset() - GAP_PX));
  }

  function run() {
    if (abandoned) return;
    var n = wantedNumber();
    if (n < 0) return;
    if (timer) clearInterval(timer);

    var started = Date.now();
    var ours = null;

    timer = setInterval(function () {
      var here = scrollY();

      /* човекът е скролнал сам -> спираме и не го връщаме обратно */
      if (ours !== null && Math.abs(here - ours) > TOLERANCE_PX) {
        abandoned = true;
        clearInterval(timer);
        timer = null;
        return;
      }

      var card = cardFor(n);
      if (card) {
        var want = desiredTop(card);
        if (Math.abs(here - want) > TOLERANCE_PX) window.scrollTo(0, want);
        ours = scrollY();
      }

      if (Date.now() - started > MAX_MS) {
        clearInterval(timer);
        timer = null;
      }
    }, STEP_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  /* снимките и Alpine се подреждат след DOMContentLoaded — втори опит на load */
  window.addEventListener('load', run);
  window.addEventListener('hashchange', function () {
    abandoned = false;
    run();
  });
})();
